import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import { NavSections, NotificationType } from 'common/enums';
import { AppContext, appActions } from 'context';
import { HelpInfoSections } from 'context/app-context/context-reducer';
import { SettingsLayout, SettingsNavItem } from 'common/settings-layout';
import cardStyles from 'common/settings-layout/settings-card.module.scss';
import { getInitials } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import React, {
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { AccountInitialState, AccountReducer } from './account-reducer';
import styles from './account.module.scss';
import { BackupInformation } from './components/backup-information/backup-information';
import { ContributorTable } from './components/contributor-table/contributor-table';
import { DiscountCodesCards } from './components/discount-codes-cards';
import { ManageBillingCard } from './components/manage-billing-card';
import { PaymentMethodCard } from './components/payment-method-card';
import { PersonalInformation } from './components/personal-information';
import { SubscriptionsTable } from './components/subscriptions-table';

const Account: React.FC = () => {
  const openAddPaymentMethodModal = !!new URLSearchParams(
    window.location.search,
  ).get('add-payment-method');

  const { state: generalState, dispatch: generalDispatch } =
    useContext(AppContext);
  const [accountState, accountDispatch] = useReducer(
    AccountReducer,
    AccountInitialState,
  );

  const [fetchingVaults, setFetchingVaults] = useState(false);
  const [fetchingContributorVaults, setFetchingContributorVaults] =
    useState<boolean>(false);

  const didMount = useRef(false);
  const concatNextVaults = useRef(false);
  const concatNextVaultsContributor = useRef(false);

  const fetchVaults = async () => {
    try {
      const { data, pagination } = await VaultsController.getVaults(
        accountState.queryParams,
      );

      const newVaults = data;

      accountDispatch({ type: 'UPDATE_VAULTS', vaults: newVaults });
      accountDispatch({ type: 'UPDATE_PAGINATION', pagination });
    } catch (err: any) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Vaults Information',
          message: 'Vaults Information fails',
          type: NotificationType.Error,
        }),
      });
    } finally {
      setFetchingVaults(false);
    }
  };

  const fetchContributorVaults = async () => {
    try {
      const { data, pagination } = await VaultsController.getVaults(
        accountState.queryParamsContributor,
      );

      const newVaults = data;

      accountDispatch({ type: 'UPDATE_VAULTS_CONTRIBUTOR', vaults: newVaults });
      accountDispatch({ type: 'UPDATE_PAGINATION_CONTRIBUTOR', pagination });
    } catch (err: any) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Vaults Information',
          message: 'Vaults Information fails',
          type: NotificationType.Error,
        }),
      });
    } finally {
      setFetchingContributorVaults(false);
    }
  };

  useEffect(() => {
    generalDispatch({
      type: appActions.ACTUAL_SECTION,
      actualSection: NavSections.account,
    });
    generalDispatch({
      type: appActions.SET_HELP_SECTION,
      helpSection: HelpInfoSections.account_settings,
    });
    fetchVaults();
    fetchContributorVaults();

    return () => {
      generalDispatch({
        type: appActions.SET_HELP_SECTION,
      });
    };
  }, []);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    setFetchingContributorVaults(true);
    fetchContributorVaults();
  }, [accountState.queryParamsContributor]);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    setFetchingVaults(true);
    fetchVaults();
  }, [accountState.queryParams]);

  useEffect(() => {
    accountDispatch({
      type: 'UPDATE_USER_NEW_DATA',
      userNewData: generalState.data.user,
    });
  }, [generalState.data.user]);

  const handleGetMoreVaults = (page: number) => {
    const newPageNum = page;
    concatNextVaults.current = true;

    const newQueryParamsCopy = { ...accountState.queryParams };
    newQueryParamsCopy.pageNum = newPageNum;

    accountDispatch({
      type: 'UPDATE_QUERY_PARAMS',
      queryParams: newQueryParamsCopy,
    });
  };

  const handleGetMoreContributorVaults = (page: number) => {
    const newPageNum = page;
    concatNextVaultsContributor.current = true;

    const newQueryParamsCopy = { ...accountState.queryParamsContributor };
    newQueryParamsCopy.pageNum = newPageNum;

    accountDispatch({
      type: 'UPDATE_QUERY_PARAMS_CONTRIBUTOR',
      queryParams: newQueryParamsCopy,
    });
  };

  const { user } = generalState.data;

  const nav = (
    <SettingsNavItem
      active
      avatar={
        <span className={styles.userAvatar}>{getInitials(user.name)}</span>
      }
      title={user.name}
      subtitle={user.email}
    />
  );

  return (
    <div className={globalStyles.loggedMainContainer}>
      <SettingsLayout activeTab="account" nav={nav}>
        <div className={styles.accountContent}>
          <div className={styles.column}>
            <PersonalInformation
              className={cardStyles.card}
              generalDispatch={generalDispatch}
              accountDispatch={accountDispatch}
              generalState={generalState}
              accountState={accountState}
            />

            <BackupInformation
              className={cardStyles.card}
              generalDispatch={generalDispatch}
              accountDispatch={accountDispatch}
              generalState={generalState}
              accountState={accountState}
            />
          </div>
          <div className={styles.column}>
            <ManageBillingCard className={cardStyles.card} />
            {/* Legacy vaults are billed off our saved card and discount
                code, so those cards stay below the Stripe billing card. */}
            {generalState.data.user.legacyBilling && (
              <div className={styles.paymentAndDiscount}>
                <PaymentMethodCard
                  className={cardStyles.card}
                  generalState={generalState}
                  openUpdatePayMethodModalAutomatically={
                    openAddPaymentMethodModal
                  }
                  titleText="Payment Method"
                  subtitleText="Vaults you own will be charged to this payment method."
                />
                <DiscountCodesCards
                  className={cardStyles.card}
                  generalDispatch={generalDispatch}
                  accountDispatch={accountDispatch}
                  generalState={generalState}
                  accountState={accountState}
                />
              </div>
            )}
            <SubscriptionsTable
              className={cardStyles.card}
              accountState={accountState}
              fetchingVaults={fetchingVaults}
              notifyGetMoreVaults={(page: number) => handleGetMoreVaults(page)}
            />
            <ContributorTable
              className={cardStyles.card}
              accountState={accountState}
              fetchingVaults={fetchingContributorVaults}
              notifyGetMoreVaults={(page: number) =>
                handleGetMoreContributorVaults(page)
              }
            />
          </div>
        </div>
      </SettingsLayout>
    </div>
  );
};

export { Account };
